/*
 *  ************************************************************************
 *  *  © [2015 - 2024] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { getPlanPrice, getPlanDuration } from '../../../layout-utils';
import { navigateToSubscriptionSuccessPage } from '../../../payment-utils';
import { getSubscriptionRedirect, getTextContrast } from '../../../../../../utils';
import { CaptureUserData } from '../../../../../molecules/capture-user-data';
import { ArrowRightIcon } from '../icons';
import './checkout-panel.m.css';

export const OrderSummarySection = ({ plan, subscriptionConfig = {}, mode, localizedTime, config }) => {
  const { orderSummaryHeading = 'Order Summary', taxNote = '(Inclusive of all taxes)' } = subscriptionConfig;
  const price = getPlanPrice(plan?.price_currency, plan?.price_cents);
  const duration = plan ? getPlanDuration(localizedTime, plan) : null;
  const toPayLabel = get(config,['pagebuilder-config', 'subscription', 'pageSetting', 'toPayLabel'], 'To Pay');
  return (
    <div styleName={`order-summary-section ${mode === 'dark' ? 'dark' : ''}`} data-test-id='subscription-order-summary'>
      <h3 styleName='order-summary-heading' data-test-id='subscription-order-summary-heading'>{orderSummaryHeading || "Order Summary"}</h3>
      {plan ? (
        <div styleName='to-pay-row'>
          <div styleName='to-pay-left'>
            <span styleName='to-pay-label' data-test-id='subscription-order-to-pay-label'>{toPayLabel || "To Pay"}</span>
            <span styleName='to-pay-subtitle'>
              <span data-test-id='subscription-order-plan-name'>{plan.title}</span>{' '}
              {duration && <span data-test-id='subscription-order-plan-duration'>{duration}</span>}
            </span>
          </div>
          <div styleName='to-pay-right'>
            <span styleName='to-pay-amount' data-test-id='subscription-order-total-amount'>{price}</span>
            <span styleName='to-pay-tax' data-test-id='subscription-order-tax-info'>{taxNote}</span>
          </div>
        </div>
      ) : (
        <p styleName='no-plan-hint'>Select a plan to view your order summary</p>
      )}
    </div>
  );
};

OrderSummarySection.propTypes = {
  plan: PropTypes.object,
  subscriptionConfig: PropTypes.object,
  mode: PropTypes.string,
  localizedTime: PropTypes.string,
  config: PropTypes.object
};

export const CheckoutPanel = ({
  selectedPlan,
  subscriptionConfig = {},
  config,
  mode,
  initLoginlessSubscription,
  navigateTo,
  member,
  isPB = false
}) => {
  const { proceedToPaymentButton, buttonColor, darkButtonColor } = subscriptionConfig;
  const primaryColor = get(config, ['pagebuilder-config', 'general', 'primaryColor'], '#4860BC');
  const darkPrimaryColor = get(config, ['pagebuilder-config', 'general', 'darkPrimaryColor'], primaryColor);

  const defaultButtonColor = buttonColor || primaryColor;
  const defaultDarkButtonColor = darkButtonColor || darkPrimaryColor || defaultButtonColor;
  const updatedButtonColor = mode === 'dark' ? defaultDarkButtonColor : defaultButtonColor;
  const subscribeButtonColor = getTextContrast(updatedButtonColor);
  const localizationConfig = get(config, ['pagebuilder-config', 'general', 'localization'], {});
  const localizedElements = get(localizationConfig, ['localizedElements'], {});
  const shippingAddressLabels =
    get(localizedElements, ['shippingAddress']) ||
    get(localizationConfig, ['shippingAddress']) ||
    {};

  const shippingHeading =
    shippingAddressLabels.heading ||
    get(subscriptionConfig, ['shippingAddressHeading']) ||
    'Shipping Details';

  const buttonText =
    shippingAddressLabels.button ||
    proceedToPaymentButton ||
    'Proceed to Payment';

  const [isLoading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const handleProceed = async (formData) => {
    if (isPB) return; // Static in PB preview
    if (!selectedPlan) return;

    setLoading(true);
    setApiError('');

    // Extract sanitized form field values
    const name = (formData.name || '').trim();
    const emailAddress = (formData.email || '').trim();
    const address = (formData.address || '').trim();
    const city = (formData.city || '').trim();
    const state = (formData.state || '').trim();
    const country = (formData.country || '').trim();
    const postCode = (formData.postCode || '').trim();
    const dialCode = (formData.dialCode || '').trim();
    const couponCode = selectedPlan?.coupon_code || selectedPlan?.couponCode || '';

    // Format phone number into international E.164 format (+<countryCode><subscriberNumber>)
    // required by AccessType and payment gateways
    const rawPhoneNumber = (formData.phoneNumber || '').trim().replace(/\s+/g, '');
    let formattedPhoneNumber = rawPhoneNumber;
    if (rawPhoneNumber && dialCode && !rawPhoneNumber.startsWith('+')) {
      const formattedDialCode = dialCode.startsWith('+') ? dialCode : `+${dialCode}`;
      formattedPhoneNumber = `${formattedDialCode}${rawPhoneNumber}`;
    }

    const metadata = {
      ...(formData.metadata || {}),
      ...(dialCode ? { dial_code: dialCode } : {})
    };

    try {
      const paymentResult = await initLoginlessSubscription({
        emailAddress: emailAddress || undefined,
        name,
        phoneNumber: formattedPhoneNumber,
        address,
        city,
        state,
        country,
        postCode,
        metadata,
        selectedPlan,
        planType: 'standard',
        couponCode
      });

      if (paymentResult) {
        if (typeof window !== 'undefined' && window.localStorage) {
          const rawDest = getSubscriptionRedirect() || '/';
          window.localStorage.setItem('loginRedirectUrl', rawDest);
        }
        navigateToSubscriptionSuccessPage({ config, navigateTo });
      }
    } catch (err) {
      const errorMsg =
        get(err, ['error', 'message']) || get(err, ['message']) || 'Something went wrong. Please try again.';
      setApiError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const primaryBtnStyle = updatedButtonColor
    ? { background: updatedButtonColor, color: subscribeButtonColor === 'dark' ? '#000' : '#fff' }
    : {};

  return (
    <div styleName={`checkout-panel ${mode === 'dark' ? 'dark' : ''} ${isPB ? 'is-pb' : ''}`} data-test-id='loginless-checkout-panel'>
      <h3 styleName='shipping-heading' data-test-id='subscription-shipping-heading'>{shippingHeading}</h3>

      <CaptureUserData
        isInline={true}
        member={member}
        subscriptionConfig={subscriptionConfig}
        onSubmit={handleProceed}
        buttonText={
          isLoading ? (
            'Processing…'
          ) : (
            <>
              <span>{buttonText}</span>
              <ArrowRightIcon width={16} height={16} stroke="currentColor" />
            </>
          )
        }
        buttonStyle={primaryBtnStyle}
        isPB={isPB}
        mode={mode}
      />
      {apiError && <p styleName='api-error'>{apiError}</p>}
    </div>
  );
};

CheckoutPanel.propTypes = {
  selectedPlan: PropTypes.object,
  subscriptionConfig: PropTypes.object,
  config: PropTypes.object,
  mode: PropTypes.string,
  initLoginlessSubscription: PropTypes.func,
  navigateTo: PropTypes.func,
  member: PropTypes.object,
  isPB: PropTypes.bool
};
